import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from './formatDate';
import { Users, Search, Shield, ShieldCheck, User, Calendar, AlertCircle, ChevronDown, Check, Lock } from 'lucide-react';

function UserManagement() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const fetchId = useRef(0);

  const fetchUsers = useCallback(async () => {
    const id = ++fetchId.current;
    try {
      setError(null);
      const data = await usersApi.getAll();
      if (id !== fetchId.current) return;
      if (!Array.isArray(data) || data.some(user => !user.id || typeof user.role !== 'string')) {
        throw new Error('Invalid user data from server');
      }
      setUsers(data);
    } catch (err) {
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    return () => { fetchId.current += 1; };
  }, [fetchUsers]);

  async function handleRoleChange(userId, newRole) {
    try {
      await usersApi.updateRole(userId, newRole);
      setActiveDropdown(null);
      if (Number(userId) === Number(currentUser?.id)) {
        await refreshUser();
        if (newRole === 'user') return;
      }
      await fetchUsers();
    } catch (err) {
      alert('Failed to update role: ' + err.message);
    }
  }

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="h-12 bg-gray-100 rounded" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-16 bg-gray-50 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load users</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={fetchUsers}
            className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#00AAFF]/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#00AAFF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-sm text-gray-500">{users.length} total users</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-64 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] transition-all"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map((user, index) => (
                <tr
                  key={user.id}
                  className="hover:bg-gray-50 transition-colors animate-slide-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#00AAFF] flex items-center justify-center text-white text-sm font-semibold">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`
                      inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold
                      ${user.role === 'superadmin'
                        ? 'bg-purple-100 text-purple-700 border border-purple-200'
                        : user.role === 'admin'
                        ? 'bg-[#00AAFF]/10 text-[#00AAFF] border border-[#00AAFF]/20'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }
                    `}>
                      {user.role === 'superadmin'
                        ? <ShieldCheck className="w-3 h-3" />
                        : user.role === 'admin'
                        ? <Shield className="w-3 h-3" />
                        : <User className="w-3 h-3" />
                      }
                      {user.role === 'superadmin' ? 'Super Admin' : user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {formatDate(user.created_at, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      }, 'en-US')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end">
                      {user.role === 'superadmin' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg">
                          <Lock className="w-3.5 h-3.5" />
                          Protected
                        </span>
                      ) : (
                        <button
                          ref={el => buttonRefs.current[user.id] = el}
                          onClick={() => {
                            if (activeDropdown === user.id) {
                              setActiveDropdown(null);
                            } else {
                              const rect = buttonRefs.current[user.id]?.getBoundingClientRect();
                              if (rect) {
                                setDropdownPosition({
                                  top: rect.bottom + 8,
                                  left: rect.right - 160
                                });
                              }
                              setActiveDropdown(user.id);
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                        >
                          Change Role
                          <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === user.id ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No users found matching your search</p>
          </div>
        )}
      </div>

      {/* Role Change Dropdown Portal */}
      {activeDropdown && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActiveDropdown(null)}
          />
          <div
            className="fixed w-40 bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-1 animate-slide-up"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            {['user', 'admin'].map(role => {
              const activeUser = users.find(u => u.id === activeDropdown);
              return (
                <button
                  key={role}
                  onClick={() => handleRoleChange(activeDropdown, role)}
                  className={`
                    w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors
                    ${activeUser?.role === role
                      ? 'text-[#00AAFF] bg-[#00AAFF]/5'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <span className="flex items-center gap-2">
                    {role === 'admin' ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </span>
                  {activeUser?.role === role && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default UserManagement;
